import { type FormEvent, useState } from "react"
import { useForm, usePage } from "@inertiajs/react"
import { Flag, X } from "lucide-react"
import { captureAnalyticsEvent } from "@/lib/analytics"
import type { PageProps } from "@/types/inertia"

type Locale = "en" | "de"

const COPY = {
  en: {
    trigger: "Report a problem",
    title: "Report a problem",
    intro: "Tell the editors what is wrong or out of date. No account is needed.",
    message: "What needs fixing?",
    messagePlaceholder: "Describe the incorrect or outdated information...",
    email: "Email (optional)",
    emailHint: "Only used if an editor needs to clarify this report.",
    submit: "Send report",
    sending: "Sending...",
    close: "Close report form",
  },
  de: {
    trigger: "Problem melden",
    title: "Problem melden",
    intro: "Teile der Redaktion mit, was falsch oder veraltet ist. Kein Konto erforderlich.",
    message: "Was muss korrigiert werden?",
    messagePlaceholder: "Beschreibe die falsche oder veraltete Information...",
    email: "E-Mail (optional)",
    emailHint: "Wird nur verwendet, falls die Redaktion Rückfragen hat.",
    submit: "Hinweis senden",
    sending: "Wird gesendet...",
    close: "Meldeformular schließen",
  },
} satisfies Record<Locale, Record<string, string>>

export function ProblemReportSheet({ locale }: { locale: Locale }) {
  const page = usePage<PageProps>()
  const context = page.props.problem_report_context
  const [open, setOpen] = useState(false)
  const copy = COPY[locale]
  const form = useForm({
    message: "",
    email: "",
    website: "",
    page_path: page.url.split("?")[0],
    locale,
    reportable_type: context?.reportable_type ?? "",
    reportable_id: context?.reportable_id ?? "",
  })

  if (!context) return null

  const submit = (event: FormEvent) => {
    event.preventDefault()
    form.post("/problem-reports", {
      preserveScroll: true,
      onSuccess: () => {
        captureAnalyticsEvent("problem_report_submitted", { reportable_type: context.reportable_type })
        form.reset("message", "email", "website")
        setOpen(false)
      },
    })
  }

  return (
    <>
      <button type="button" onClick={() => { captureAnalyticsEvent("problem_report_opened", { reportable_type: context.reportable_type }); setOpen(true) }} className="fixed right-4 bottom-4 z-40 inline-flex items-center gap-2 rounded-full border border-hairline-strong bg-surface px-4 py-2.5 text-sm font-semibold text-ink-secondary shadow-lg hover:text-ink-display">
        <Flag className="h-4 w-4" aria-hidden />{copy.trigger}
      </button>
      {open && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-ink-display/45 p-0 backdrop-blur-sm sm:items-center sm:p-5" role="presentation" onMouseDown={(event) => { if (event.currentTarget === event.target) setOpen(false) }}>
          <section data-analytics-private role="dialog" aria-modal="true" aria-labelledby="problem-report-title" className="ph-no-capture w-full max-w-lg rounded-t-2xl border border-hairline bg-surface p-5 shadow-2xl sm:rounded-2xl sm:p-6">
            <div className="flex items-start gap-4">
              <div className="min-w-0 flex-1">
                <p className="public-kicker">Xplore Croatia</p>
                <h2 id="problem-report-title" className="public-section-title mt-1">{copy.title}</h2>
                <p className="mt-2 text-sm text-ink-muted">{copy.intro}</p>
              </div>
              <button type="button" onClick={() => setOpen(false)} aria-label={copy.close} className="rounded-full border border-hairline p-2 text-ink-muted"><X className="h-4 w-4" /></button>
            </div>
            <form onSubmit={submit} className="mt-5 space-y-4">
              <label className="block text-sm font-semibold text-ink-secondary">
                {copy.message}
                <textarea required maxLength={2000} value={form.data.message} onChange={(event) => form.setData("message", event.target.value)} placeholder={copy.messagePlaceholder} className="mt-1.5 min-h-32 w-full rounded-xl border border-hairline-strong bg-surface px-3.5 py-3 font-normal outline-none focus:border-accent" />
                {form.errors.message && <span className="mt-1 block text-xs text-danger">{form.errors.message}</span>}
              </label>
              <label className="block text-sm font-semibold text-ink-secondary">
                {copy.email}
                <input type="email" maxLength={254} value={form.data.email} onChange={(event) => form.setData("email", event.target.value)} className="mt-1.5 w-full rounded-xl border border-hairline-strong bg-surface px-3.5 py-2.5 font-normal outline-none focus:border-accent" />
                <span className="mt-1 block text-xs font-normal text-ink-muted">{copy.emailHint}</span>
                {form.errors.email && <span className="mt-1 block text-xs text-danger">{form.errors.email}</span>}
              </label>
              <label className="absolute -left-[10000px]" aria-hidden="true">
                Website
                <input tabIndex={-1} autoComplete="off" value={form.data.website} onChange={(event) => form.setData("website", event.target.value)} />
              </label>
              <button type="submit" disabled={form.processing} className="btn btn-primary w-full">{form.processing ? copy.sending : copy.submit}</button>
            </form>
          </section>
        </div>
      )}
    </>
  )
}
